// ─── API Client ─────────────────────────────────────────────────────────
// Typed fetch wrapper for dashboard-api communication.
// Base URL from VITE_API_URL (build-time) or empty string (local dev proxy).
// All requests include credentials for httpOnly cookie auth.

const BASE_URL = import.meta.env.VITE_API_URL || '';

interface ApiEnvelope<T> {
	data: T;
	error: null;
	meta: Record<string, unknown> | null;
}

interface ApiErrorResponse {
	data: null;
	error: { message: string; code?: string };
	meta: null;
}

export class ApiError extends Error {
	constructor(
		public readonly status: number,
		message: string,
		public readonly code?: string,
	) {
		super(message);
		this.name = 'ApiError';
	}
}

async function _fetch<T>(path: string, init?: RequestInit): Promise<T> {
	const url = `${BASE_URL}${path}`;
	const res = await fetch(url, {
		...init,
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			...init?.headers,
		},
	});

	if (!res.ok) {
		let message = `Request failed: ${res.status}`;
		let code: string | undefined;
		try {
			const body = (await res.json()) as ApiErrorResponse;
			if (body.error?.message) {
				message = body.error.message;
				code = body.error.code;
			}
		} catch {
			// Use default message
		}
		throw new ApiError(res.status, message, code);
	}

	const body = (await res.json()) as ApiEnvelope<T>;
	return body.data;
}

/** GET request returning data */
export async function get<T>(path: string): Promise<T> {
	return _fetch<T>(path);
}

/** POST request returning data */
export async function post<T>(path: string, body?: unknown): Promise<T> {
	return _fetch<T>(path, {
		method: 'POST',
		body: body != null ? JSON.stringify(body) : undefined,
	});
}

/** PUT request returning data */
export async function put<T>(path: string, body?: unknown): Promise<T> {
	return _fetch<T>(path, {
		method: 'PUT',
		body: body != null ? JSON.stringify(body) : undefined,
	});
}

/** DELETE request returning data */
export async function del<T>(path: string): Promise<T> {
	return _fetch<T>(path, { method: 'DELETE' });
}
